import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, Users, ShoppingBag, Eye, AlertTriangle, Package } from "lucide-react";

function fmtINR(n) { return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0); }
function fmtUSD(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0); }

const STAT = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border border-white/8 bg-midnight p-6" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-[10px] tracking-[0.32em] uppercase text-silver/70">{label}</div>
      <Icon size={16} strokeWidth={1.2} className="text-silver" />
    </div>
    <div className="font-serif text-white text-3xl md:text-4xl mt-3">{value}</div>
    {sub && <div className="text-xs text-white/45 mt-1">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics/summary")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-white/50">Loading…</div>;
  if (!data) return <div className="text-white/50">No data.</div>;

  const t = data.totals;
  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <div>
        <div className="eyebrow text-silver/70 mb-2">Overview</div>
        <h1 className="font-serif text-white text-3xl md:text-4xl">Atelier dashboard.</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <STAT testid="stat-orders" icon={ShoppingBag} label="Orders" value={t.orders} sub={`${data.orders_by_status.confirmed || 0} confirmed · ${data.orders_by_status.shipped || 0} shipped`} />
        <STAT testid="stat-revenue-inr" icon={TrendingUp} label="Revenue · INR" value={fmtINR(t.revenue_inr)} sub="Paid orders" />
        <STAT testid="stat-revenue-usd" icon={TrendingUp} label="Revenue · USD" value={fmtUSD(t.revenue_usd)} sub="Paid orders" />
        <STAT testid="stat-customers" icon={Users} label="Customers" value={t.customers} sub={`${t.new_customers_30d} new · 30d`} />
        <STAT testid="stat-visitors" icon={Eye} label="Visits · 7d" value={t.visits_7d} sub={`${t.unique_visitors_30d} unique · 30d`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="border border-white/8 bg-midnight p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-white text-xl">Best sellers</h2>
            <span className="text-[10px] tracking-[0.3em] uppercase text-silver/70">By qty sold</span>
          </div>
          {data.bestsellers.length === 0 ? (
            <div className="text-white/45 text-sm">No paid orders yet.</div>
          ) : (
            <ul className="space-y-3">
              {data.bestsellers.map((b) => (
                <li key={b.product_id} className="flex items-center gap-3" data-testid={`bestseller-${b.product_id}`}>
                  {b.image && <img src={b.image} alt="" className="w-10 h-12 object-cover bg-midnight-panel" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-white text-base truncate">{b.name || b.product_id}</div>
                  </div>
                  <div className="text-silver text-sm tracking-[0.2em]">×{b.quantity_sold}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border border-white/8 bg-midnight p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-white text-xl">Most viewed</h2>
            <span className="text-[10px] tracking-[0.3em] uppercase text-silver/70">30 days</span>
          </div>
          {data.most_viewed.length === 0 ? (
            <div className="text-white/45 text-sm">No views logged yet.</div>
          ) : (
            <ul className="space-y-3">
              {data.most_viewed.map((m) => (
                <li key={m.product_id} className="flex items-center gap-3">
                  {m.image && <img src={m.image} alt="" className="w-10 h-12 object-cover bg-midnight-panel" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-white text-base truncate">{m.name || m.product_id}</div>
                  </div>
                  <div className="text-silver text-sm tracking-[0.2em]">{m.views} views</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {data.low_stock.length > 0 && (
        <section className="border border-silver/40 bg-white/5 p-6" data-testid="low-stock-alerts">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-silver" strokeWidth={1.3} />
            <h2 className="eyebrow text-silver">Low stock alerts</h2>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.low_stock.map((p) => (
              <li key={p.id} className="flex items-center gap-3 border border-white/8 bg-midnight p-3">
                {p.image && <img src={p.image} alt="" className="w-10 h-12 object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-white text-base truncate">{p.name}</div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">{p.id}</div>
                </div>
                <div className="text-silver text-sm">{p.stock} left</div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
